/**
 * Google Cloud Text-to-Speech Service
 *
 * This service uses Google Cloud's Text-to-Speech API with Chirp 3 Studio voices
 * to generate high-quality audio files from text content.
 */

export interface TTSVoice {
  name: string;
  displayName: string;
  languageCode: string;
  gender: 'MALE' | 'FEMALE' | 'NEUTRAL';
}

// Available Chirp 3 Studio voices
export const CHIRP_VOICES: TTSVoice[] = [
  {
    name: 'en-US-Studio-O',
    displayName: 'Studio O (Female)',
    languageCode: 'en-US',
    gender: 'FEMALE'
  },
  {
    name: 'en-US-Studio-Q',
    displayName: 'Studio Q (Male)',
    languageCode: 'en-US',
    gender: 'MALE'
  },
  {
    name: 'en-US-Journey-D',
    displayName: 'Journey D (Male)',
    languageCode: 'en-US',
    gender: 'MALE'
  },
  {
    name: 'en-US-Journey-F',
    displayName: 'Journey F (Female)',
    languageCode: 'en-US',
    gender: 'FEMALE'
  },
];

export interface GenerateAudioOptions {
  text: string;
  voiceName?: string;
  speakingRate?: number; // 0.25 to 4.0
  pitch?: number; // -20.0 to 20.0
}

/**
 * Generate audio from text using Google Cloud Text-to-Speech API
 * Returns base64-encoded audio data
 */
export const generateAudio = async (options: GenerateAudioOptions): Promise<string> => {
  const {
    text,
    voiceName = 'en-US-Studio-O',
    speakingRate = 1.0,
    pitch = 0.0
  } = options;

  const apiKey = import.meta.env.VITE_GOOGLE_CLOUD_API_KEY;

  if (!apiKey) {
    throw new Error('Google Cloud API key not configured. Please set VITE_GOOGLE_CLOUD_API_KEY environment variable.');
  }

  try {
    const response = await fetch(
      `https://texttospeech.googleapis.com/v1/text:synthesize?key=${apiKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          input: {
            text: text
          },
          voice: {
            languageCode: 'en-US',
            name: voiceName
          },
          audioConfig: {
            audioEncoding: 'MP3',
            speakingRate: speakingRate,
            pitch: pitch,
            effectsProfileId: ['headphone-class-device'], // Optimize for headphones
            sampleRateHertz: 24000 // High quality
          }
        })
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`TTS API Error: ${errorData.error?.message || response.statusText}`);
    }

    const data = await response.json();

    if (!data.audioContent) {
      throw new Error('No audio content returned from API');
    }

    return data.audioContent; // Base64-encoded audio
  } catch (error) {
    console.error('Google Cloud TTS Error:', error);
    throw error;
  }
};

/**
 * Convert base64 audio to a Blob
 */
export const base64ToBlob = (base64: string, mimeType: string = 'audio/mp3'): Blob => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
};

/**
 * Estimate the cost of generating audio for given text
 * Google Cloud TTS pricing: $16 per 1 million characters for Studio voices
 */
export const estimateCost = (text: string): number => {
  const characterCount = text.length;
  const costPerMillionChars = 16.0;
  return (characterCount / 1000000) * costPerMillionChars;
};

/**
 * Calculate estimated audio duration based on text length
 * Average speaking rate is ~150 words per minute
 */
export const estimateDuration = (text: string, speakingRate: number = 1.0): number => {
  const wordCount = text.split(/\s+/).length;
  const durationInMinutes = wordCount / (150 * speakingRate);
  return Math.ceil(durationInMinutes * 60); // Return seconds
};
